import axios from 'axios';
import { APIV2 } from '../../../backend';
import { useAppContext } from '../../../hooks/context/AppContext';
import { useState, useMemo } from 'react';
import useSilentIdTokenRefresh from "../../../msal/useSilentTokenRefresh";

// Common error handler
const handleError = (error: unknown, context: string) => {
  console.error(`Error ${context}:`, error);
  return null;
};

// SHEQ Payload Interface
export interface SheqPayload {
  Risk_Key: string;
  Hygiene_Risk: string;
  TransitTrials_Key: string;
  Seac_Risk: string;
  Plastic_Inclusion_Key: string;
  Ghg_Impact: string;
  Su_Cm_Quality_Approved: string;
  Technical_Assessment_Risk: string;
  Comment: string;
};

// Custom hook for API client
export const useApiClient = () => {
  const { authData } = useAppContext();
  const [idToken, setIdToken] = useState(null);
  const getIdTokenSilently = useSilentIdTokenRefresh((token: any | null, account: any | null) => {
    setIdToken(token);
  });

  const apiClient = useMemo(() => {
    const instance = axios.create({
      baseURL: APIV2,
      headers: {
        'Content-Type': 'application/json',
        Authorization: idToken ? `Bearer ${idToken}` : `Bearer ${authData.idToken}`,
      },
    });

    // Add response interceptor to handle 401 errors
    instance.interceptors.response.use(
      response => response,
      async (error) => {
        const originalRequest = error.config;

        // If the error is 401 and we haven't already retried
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const {token}  = await getIdTokenSilently();
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            }
          } catch (refreshError) {
            console.error('Error refreshing token:', refreshError);
            // If token refresh fails, redirect to login or handle as needed
            // You might want to add your auth redirect logic here
            window.location.reload();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
    return instance;
  }, [idToken, authData.idToken]);

  // Risk API
  const getRisk = async () => {
    try {
      const response = await apiClient.get('/risks');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching risks');
    }
  };

  // Transit Trials API
  const getTransitTrails = async () => {
    try {
      const response = await apiClient.get('/lookup/SHEQ_Transit_Trials');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching transit trails');
    }
  };

  // Plastic Inclusion API
  const getPlastciInclusion = async () => {
    try {
      const response = await apiClient.get('/lookup/SHEQ_Plastic_Inclusion');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching plastic inclusion');
    }
  };

  // SHEQ Data API
  const createSheq = async (payload: SheqPayload, project_key: string) => {
    try {
      const response = await apiClient.post(`/sheqsustainability/${project_key}`, payload);
      return response.data;
    } catch (err) {
      return handleError(err, 'creating SHEQ');
    }
  };

  const updateSheq = async (payload: SheqPayload, project_key: string) => {
    try {
      const response = await apiClient.put(`/sheqsustainability/${project_key}`, payload);
      return response.data;
    } catch (err) {
      return handleError(err, 'updating SHEQ');
    }
  };

  const getSheq = async (project_key: string) => {
    try {
      const response = await apiClient.get(`/sheqsustainability/${project_key}`);
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching SHEQ');
    }
  };
  const getHygiene = async () => {
    try {
      const response = await apiClient.get('/lookup/SHEQ_Hygiene_Risk');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching hygiene');
    }
  };

  const getTechnicalAssesmentRisks = async () => {
    try {
      const response = await apiClient.get('/lookup/SHEQ_Technical_Risk_Assessment');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching technical assessment risks');
    }
  };

  const getSeacRisks = async () => {
    try {
      const response = await apiClient.get('/lookup/SHEQ_SEAC_Risk');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching SEAC risks');
    }
  };

  const getGhgImpact = async () => {
    try {
      const response = await apiClient.get('/lookup/SHEQ_GHG_Impact');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching GHG impact');
    }
  };

  const getQualityApproved = async () => {
    try {
      const response = await apiClient.get('/lookup/SHEQ_Quality_Approved');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching Quality Approved');
    }
  };

  
  // Lookup data functions
  // const gethygiene = () => [
  //   { key: 1, value: 'Yes' },
  //   { key: 2, value: 'No' },
  // ];


  // const getTechnicalAssesmentRisks = () => [
  //   { key: 1, value: 'Yes' },
  //   { key: 2, value: 'No' },
  // ];

  // const getSeacRisks = () => [
  //   { key: 1, value: 'Yes' },
  //   { key: 2, value: 'No' },
  // ];

  // const getGhgImpact = () => [
  //   { key: 1, value: 'Yes' },
  //   { key: 2, value: 'No' },
  // ];

  // const getQualityApproved = () => [
  //   { key: 1, value: 'Yes' },
  //   { key: 2, value: 'No' },
  // ];

  return {
    getRisk,
    getTransitTrails,
    getPlastciInclusion,
    createSheq,
    updateSheq,
    getSheq,
    getHygiene,
    getTechnicalAssesmentRisks,
    getSeacRisks,
    getGhgImpact,
    getQualityApproved,
  };
};